import { useReducer, useEffect, useCallback, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { ChatHeader, MessageList, ChatInput } from '@/components/chat'
import { useConversationBootstrap } from '@/hooks/chat/useConversationBootstrap'
import { useStreamingChat } from '@/hooks/chat/useStreamingChat'
import { useChatComposer } from '@/hooks/chat/useChatComposer'
import { chatReducer, initialChatState } from '@/state/chatReducer'
import { generateMessageId } from '@/utils/chat/ids'

export function ConversationPage() {
  const { conversationId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  
  const [state, dispatch] = useReducer(chatReducer, initialChatState)
  const { send, cancel } = useStreamingChat(dispatch)
  const { setInput, addMention, removeMention, buildRequest, clearInput } = useChatComposer(
    state,
    dispatch,
    state.currentConversationId
  )
  
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [state.messages, state.streamingMessage])
  
  useConversationBootstrap(
    { conversationId },
    location.state as any,
    dispatch
  )

  // Cancel streaming on conversation change or unmount
  useEffect(() => {
    return cancel
  }, [conversationId, cancel])

  // Handle initial message if needed
  useEffect(() => {
    const navState = location.state as any
    if (
      state.initStatus === 'ready' &&
      state.currentConversationId &&
      navState?.initialMessage &&
      navState?.status === 'notInitialized'
    ) {
      ;(async () => {
        await handleSendMessage(navState.initialMessage, navState.tags || [])
        // Mark as initialized so we don't resend on re-renders
        navigate(`/chat/${state.currentConversationId}`, {
          replace: true,
          state: { ...navState, status: 'initialized' }
        })
      })()
    }
  }, [state.initStatus, state.currentConversationId, location.state, navigate])

  const handleSendMessage = useCallback(async (message?: string, messageTags?: any[]) => {
    const messageToSend = message || state.inputValue.trim()
    const tagsToUse = messageTags || state.tags
    
    if (!messageToSend || state.streamStatus !== 'idle' || !state.currentConversationId) return

    // Add user message immediately
    const userMessage = {
      message_id: generateMessageId(),
      content: messageToSend,
      role: 'user' as const,
      timestamp: Date.now()
    }
    dispatch({ type: 'ADD_USER_MESSAGE', payload: userMessage })
    
    // Clear input if using current input
    if (!message) {
      clearInput()
    }

    // Build and send request
    const request = buildRequest(messageToSend, tagsToUse)
    await send(state.currentConversationId, request)
  }, [state.inputValue, state.tags, state.streamStatus, state.currentConversationId, dispatch, clearInput, buildRequest, send])

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }, [handleSendMessage])

  const isLoading = state.streamStatus === 'sending'
  const isStreaming = state.streamStatus === 'streaming'
  const isDisabled = isLoading || isStreaming

  return (
    <div className="flex flex-col h-full bg-background -m-6">
      <ChatHeader title={state.conversationTitle} />
      
      <div className="flex-1 overflow-auto px-6 space-y-4">
        <MessageList
          messages={state.messages}
          streamingMessage={state.streamingMessage}
          streamStatus={state.streamStatus}
          isLoading={isLoading}
        />
        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        value={state.inputValue}
        onChange={setInput}
        onKeyPress={handleKeyPress}
        onMentionAdd={addMention}
        onMentionRemove={removeMention}
        onSend={() => handleSendMessage()}
        disabled={isDisabled}
        placeholder="Type your message... Use @ to mention target usernames"
      />
    </div>
  )
} 